import type { LocalRefLabel } from "@/utils/i18n/t11n"
import { isNullOrUndefined } from "@/utils/ts-utils"
import type { Prettify } from "@/utils/types/utils"

type RelInfo = {
  function?: string | null
  isDefault?: boolean | null
  role?: LocalRefLabel | null
  rank?: LocalRefLabel | null
  relDateOfEntry?: string
  relDateOfLeaving?: string | null
  metadatas?: { [k: string]: string }
  isPublished?: boolean | null
  isPublishedInDirectory?: boolean | null
  status?: LocalRefLabel | null
}

type UserRelInfo = {
  isUserDefault?: boolean | null
}

type Rel<T> = {
  id: string
  collaborator?: T
} & RelInfo

type ExtendedRel<T> = Prettify<T & RelInfo>
type ExtendedUserRel<T> = Prettify<T & UserRelInfo>

export const getRelatedCollaborator = <TCollaborator>(rel: Rel<TCollaborator>) => {
  return {
    ...rel.collaborator,
    function: "function" in rel ? rel.function : undefined,
    isDefault: "isDefault" in rel ? rel.isDefault : undefined,
    role: "role" in rel ? rel.role : undefined,
    rank: "rank" in rel ? rel.rank : undefined,
    relDateOfEntry: "dateOfEntry" in rel ? rel.dateOfEntry : undefined,
    relDateOfLeaving: "dateOfLeaving" in rel ? rel.dateOfLeaving : undefined,
    metadatas: "metadatas" in rel ? rel.metadatas : undefined,
    isPublished: "isPublished" in rel ? rel.isPublished : undefined,
    isPublishedInDirectory:
      "isPublishedInDirectory" in rel ? rel.isPublishedInDirectory : undefined,
    status: "status" in rel ? rel.status : undefined,
  } as NonNullable<ExtendedRel<TCollaborator>>
}

export const getRelatedCollaborators = <TCollaborator>(rels?: Rel<TCollaborator>[]) => {
  return isNullOrUndefined(rels) ? undefined : rels.map(rel => getRelatedCollaborator(rel))
}

export const getDefaultRelatedCollaborator = <TCollaborator>(
  rels?: Rel<TCollaborator>[],
  filterOn?: { [k in keyof Rel<TCollaborator>]?: Rel<TCollaborator>[k] }
) => {
  const filters = (
    isNullOrUndefined(filterOn) ? [] : Object.keys(filterOn)
  ) as (keyof Rel<TCollaborator>)[]

  const r = (rels ?? []).filter(rl => {
    let ok = true
    filters.map(f => {
      if (rl[f] !== filterOn?.[f]) ok = false
    })
    return ok
  })

  for (const rel of r) {
    if (rel.isDefault) return getRelatedCollaborator(rel)
  }

  return isNullOrUndefined(r[0]) ? undefined : getRelatedCollaborator(r[0])
}

export const getDefaultCollaborator = <TCollaborator>(rels?: ExtendedRel<TCollaborator>[]) => {
  for (const rel of rels ?? []) {
    if (rel.isDefault) return rel
  }

  const v = rels?.[0]
  return isNullOrUndefined(v) ? undefined : v
}

export const getUserDefaultCollaborator = <TCollaborator>(
  rels?: ExtendedUserRel<TCollaborator>[]
) => {
  for (const rel of rels ?? []) {
    if (rel.isUserDefault) return rel
  }

  const v = rels?.[0]
  return isNullOrUndefined(v) ? undefined : v
}
